/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  Badge,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Spinner,
  Accordion,
  Flex,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
} from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import { meetViewStyle } from '../meet/MeetVide.styles';
import { useStoreActions, useStoreState } from '../../models/hooks';
import { CandidateInfoForGuest } from '../meet/MeetTokenResponse';
import colors from '../../styles/colors';
import { formattedPhone } from '../../utils/PhoneUtils';
import { GuestInterviewRating } from './GuestInterviewRating';
import { CollaboratorNotesView } from './CollaboratorNotesView';
import { Profile } from '../messaging/seeker/candidate/profile/Profile';
import { Candidate, CandidateStatus } from '../../firebase/firestore/documents/candidate';
import ProfileQuestions from '../administration/topCandidates/ProfileQuestions';
import { useSingleSeeker } from '../../app/hooks/useSingleSeeker';

enum Tab {
  NOTES,
  CANDIDATE_INFO,
}

function InterviewCandidateInformation({
  candidateId,
  showReviewRating,
  setShowReviewRating,
  quitRoom,
  singleCandidateData,
  showCandidateAssessmentButton,
}: {
  candidateId: string;
  showReviewRating: boolean;
  setShowReviewRating: (isOpen: boolean) => void;
  quitRoom: () => void;
  singleCandidateData: Candidate | undefined;
  // eslint-disable-next-line react/require-default-props
  showCandidateAssessmentButton?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [candidateInfo, setCandidateInfo] = useState<CandidateInfoForGuest | undefined>();

  const [isloading, setIsLoading] = useState<boolean>(false);
  const [isNoteSaving, setIsNoteSaving] = useState<boolean>(false);
  const [reloadNotes, setReloadNotes] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.CANDIDATE_INFO);
  const { getCandidateDataOfMeet } = useStoreActions((actions) => actions.meet);
  const { saveNote } = useStoreActions((actions) => actions.account);
  const accountId = useStoreState((actions) => actions.app.accounts?.id);
  const candidateData = candidateId.split('_');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [noteContent, setNoteContent] = useState<string>('');

  const candidates = useStoreState((s) => s.candidate.selectedCandidates);
  const seekerData = useSingleSeeker(candidateData[1]);

  const fetchCandidateInfo = useCallback(async () => {
    setIsLoading(true);
    await getCandidateDataOfMeet({ positionId: candidateData[0], seekerId: candidateData[1] })
      .then((response) => {
        setCandidateInfo(response as any);
      })
      .catch((err) => {
        console.log('error:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchCandidateInfo();
  }, [fetchCandidateInfo]);

  // istanbul ignore next
  const submitNote = async () => {
    setIsNoteSaving(true);
    if (accountId) {
      await saveNote({
        accountId,
        positionId: candidateData[0],
        seekerId: candidateData[1],
        note: noteContent,
      });
      setIsNoteSaving(false);
      setReloadNotes(true);
      setNoteContent('');
      onClose();
    }
  };

  // istanbul ignore next
  const filteredCandidates = (candidateList: Candidate[] | undefined): Candidate[] => {
    if (!candidateList) {
      return [];
    }
    return candidateList?.filter(
      (candidate) =>
        candidate.computedStatus?.status === CandidateStatus.OfferMade ||
        candidate.computedStatus?.status === CandidateStatus.Hired ||
        candidate.computedStatus?.status === CandidateStatus.HireInReview,
    );
  };

  return (
    <Box css={meetViewStyle}>
      <Box>
        <HStack
          alignItems={['flex-start', 'center']}
          justifyContent="space-between"
          flexDirection={['column', 'row']}
          className="candidate-card-header"
          py={4}
          px={4}
          borderBottom="1px solid #E5E7EB"
          spacing={2}
        >
          <Badge colorScheme="blue" variant="subtle" textTransform="capitalize" px={3} py={1} borderRadius={2} color="blue.500">
            {t('common:candidateDetailsDrawer.details')}
          </Badge>
          <Flex mt={[2, 0]}>
            <Button data-testid="add-note-btn" size="sm" colorScheme="blue" onClick={onOpen} mr={2}>
              {t('common:interviewCandidate.addNote')}
            </Button>
            {candidateInfo && showCandidateAssessmentButton && (
              <GuestInterviewRating
                candidateInfo={candidateInfo}
                showReviewRating={showReviewRating}
                setShowReviewRating={setShowReviewRating}
                quitRoom={quitRoom}
                candidateId={candidateId}
              />
            )}
          </Flex>
        </HStack>
        <Box fontSize={['xs', 'sm']}>
          <Box p={[3, 4]} className="position-detail">
            <Heading as="h4" fontSize={['sm', 'md']} lineHeight={1.1} mb={2}>
              {t('common:candidateDetailsDrawer.positionDetails')}
            </Heading>
            {isloading ? (
              <Flex alignItems="center" justifyContent="center" minH="200px">
                <Spinner color="blue.500" />
              </Flex>
            ) : (
              <Box>
                <Text fontWeight={500}>{candidateInfo?.businessLocation}</Text>
                <Text fontWeight={600}>{candidateInfo?.position}</Text>
                <Text fontWeight={400}>{candidateInfo?.businessCompleteAddress}</Text>
              </Box>
            )}
            <Box position="relative" top={4} borderBottom="1px solid #E5E7EB" />
          </Box>
          <Box p={[3, 4]} className="candidate-detail">
            <Flex borderBottom="1px solid #E5E7EB" alignItems="center" fontWeight="bold" fontSize={['xs', 'sm']}>
              <Box
                flex={['1', 'unset']}
                textAlign={['center', 'left']}
                px={[2, 4]}
                py={2}
                cursor="pointer"
                data-testid="candidate-info-tab"
                bg={activeTab === Tab.CANDIDATE_INFO ? 'blue.100' : 'transparent'}
                color={activeTab === Tab.CANDIDATE_INFO ? 'blue.500' : colors.black}
                onClick={() => setActiveTab(Tab.CANDIDATE_INFO)}
              >
                {t('common:candidateDetailsDrawer.candidateDetail')}
              </Box>
              <Box
                flex={['1', 'unset']}
                textAlign={['center', 'left']}
                px={[2, 4]}
                py={2}
                cursor="pointer"
                data-testid="notes-tab"
                bg={activeTab === Tab.NOTES ? 'blue.100' : 'transparent'}
                color={activeTab === Tab.NOTES ? 'blue.500' : colors.black}
                onClick={() => {
                  setActiveTab(Tab.NOTES);
                  setReloadNotes(true);
                }}
              >
                Notes
              </Box>
            </Flex>
            {activeTab === Tab.NOTES ? (
              <CollaboratorNotesView
                positionId={candidateData[0]}
                seekerId={candidateData[1]}
                reloadNotes={reloadNotes}
                setReloadNotes={setReloadNotes}
              />
            ) : isloading ? (
              <Flex alignItems="center" justifyContent="center" minH="200px">
                <Spinner color="blue.500" />
              </Flex>
            ) : (
              <Box pt={4}>
                <List spacing={2}>
                  <ListItem pl={[1, 3]}>
                    <Text fontWeight={700} color="gray.700">
                      {t('common:candidateDetailsDrawer.candidateName')}
                    </Text>
                    <Text fontWeight={400} color="gray.500">
                      {candidateInfo?.name}
                    </Text>
                  </ListItem>
                  <ListItem pl={[1, 3]}>
                    <Text fontWeight={700} color="gray.700">
                      {t('common:candidateDetailsDrawer.email')}
                    </Text>
                    <Text fontWeight={400} color="gray.500" wordBreak="break-all">
                      {candidateInfo?.email}
                    </Text>
                  </ListItem>
                  <ListItem pl={[1, 3]}>
                    <Text fontWeight={700} color="gray.700">
                      {t('common:candidateDetailsDrawer.phone')}
                    </Text>
                    <Text fontWeight={400} color="gray.500">
                      {formattedPhone(candidateInfo?.phone ?? '', singleCandidateData?.countryCode)}
                    </Text>
                  </ListItem>

                  <Profile
                    hiredAndOfferedCandidates={filteredCandidates(candidates)}
                    isFromCollaborator
                    candidateFromCollaborator={singleCandidateData}
                    seekerFromCollaborator={seekerData}
                  />

                  <ListItem>
                    <Accordion defaultIndex={[0]} allowMultiple>
                      <AccordionItem
                        border="none"
                        p="0px"
                        pr={['4px', '12px']}
                        pl={['4px', '10px']}
                        data-testid="questionSection"
                      >
                        <h2>
                          <AccordionButton p="12px 0px" _focus={{ outline: 'none' }} data-testid="questionBtn">
                            <Box as="span" flex="1" textAlign="left" p="0px" fontSize={['12px', '14px']} fontWeight={700}>
                              {t('common:tabs.questions')}
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel px={0}>
                          {candidateId && <ProfileQuestions candidateId={candidateId} positionId={candidateData[0]} />}
                        </AccordionPanel>
                      </AccordionItem>
                    </Accordion>
                  </ListItem>
                </List>
              </Box>
            )}
          </Box>
          <Modal isCentered onClose={onClose} isOpen={isOpen} scrollBehavior="inside" motionPreset="slideInBottom">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('common:interviewCandidate.addNote')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody maxH={{ base: '80%', md: 'auto' }}>
                <Box w="100%" mb="40px">
                  <ReactQuill
                    style={{ height: '45vh' }}
                    data-testid="quill-editor"
                    value={noteContent}
                    onChange={setNoteContent}
                  />
                </Box>
              </ModalBody>
              <ModalFooter flexDirection={['column', 'row']} pb={[6, 4]}>
                <Button
                  isLoading={isNoteSaving}
                  disabled={isNoteSaving}
                  colorScheme="blue"
                  size="md"
                  w={['full', 'auto']}
                  mb={[2, 0]}
                  mr={[0, 4]}
                  onClick={submitNote}
                  data-testid="save-note-button"
                >
                  {t('common:interviewCandidate.saveNote')}
                </Button>
                <Button variant="ghost" w={['full', 'auto']} onClick={onClose}>
                  {t('common:videoInterviewReminder.close')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Box>
      </Box>
    </Box>
  );
}

export default InterviewCandidateInformation;
